import { useRef, useState } from "react";
import { PWHT_GRAFIK_ALANLARI, hedefEsit, pwhtEtiketi, type GelenHedef, type PwhtKarti } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  OKUMA_UCU,
  okumaDugmesi,
  okumaEylemleri,
  pwhtKanalMetni,
  pwhtKanalSonucu,
  pwhtKaynakGrafikte,
  pwhtKaynakOasis,
  pwhtKaynakSonucu,
  pwhtNedenMetni,
  pwhtNotMetinleri,
  pwhtSayfalari,
  pwhtSureMetni,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 2 · PR 11: Documents kartının PWHT "Document reading" bölümü (prototip belgeler--pwht-okuma, kart 1-2). Okuma yalnız kuralların kullandığı
 * değerleri gösterir (sözleşme §6): grafikte 5, kanalda 3 değer. Aynı grafik görüntüsü başka bir satırda okunmuşsa okuma kopyalanmıştır ("Read once").
 * OASIS işi önerir; "Use this place" kartın bugünkü tür+yer kaydını çağırır (yeri kişi kaydeder; okuma yazmaz), "Use these pages" yalnız formu
 * doldurur. Okunan süre OASIS'in PWHT imzasının sabit yazısının yanında gösterilir, onun yerine geçmez; Weld map ekranı değişmez. Uyarıların kararı
 * PR 8'in kutusudur (`UyariSatiri`). `salt` kapanmış satır içindir: form ve karar düğmeleri yok (G34).
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";
const SATIR = "grid grid-cols-[4.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 px-2.5 py-2 @min-[40rem]:grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1.3fr)]";

const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);
const durumTonu = (d: "kisi" | "oneri" | "yok") => (d === "kisi" ? "ok" : d === "oneri" ? "info" : "neutral");
/** Grafik dışı sayfaların aralıkları ("1-2", "5"); hiç yoksa boş. */
function disaridakiSayfalar(toplam: number, grafikler: readonly number[]): string[] {
  const l: string[] = [];
  let bas: number | null = null;
  for (let p = 1; p <= toplam + 1; p++) {
    const disarida = p <= toplam && !grafikler.includes(p);
    if (disarida && bas === null) bas = p;
    if (!disarida && bas !== null) { l.push(aralik({ bas, son: p - 1 })); bas = null; }
  }
  return l;
}

export interface PwhtFormYardimi {
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
  /** "Use these pages": Pages alanı (Partial) */
  sayfalar: (s: string) => void;
}

export function PwhtOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: PwhtKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: PwhtFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;
  const y = k.yer;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  /** KIM-02 açıklaması: yerin riseri (yer metninin ilk parçası) ve uyarının hattı. */
  const uyariYeri = (hat: string | null) => {
    const rn = y?.yerMetni?.split(" · ")[0];
    return rn && hat ? { rn, hat } : null;
  };
  const notlar = k.karsilastirma ? pwhtNotMetinleri(k.karsilastirma.notlar) : [];
  const toplamUyari = k.karsilastirma?.uyarilar.length ?? 0;
  const kanalSayisi = k.grafikler.reduce((t, g) => t + g.kanallar.length, 0);
  const sayfalar = pwhtSayfalari(k);
  const disarida = o && o.belgeSayisi > k.grafikler.length ? disaridakiSayfalar(o.sayfaSayisi, k.grafikler.map((g) => g.sayfa)) : [];

  return (
    <div data-bolum="pwht-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the PWHT chart for you to check. Nothing is filed or changed.")}</p>}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              sayfalar ? cevir("PWHT chart p.{0}", sayfalar) : null,
              k.kod ? cevir("Reading job {0}", k.kod) : null,
              cevir("{0} charts, {1} channels", k.grafikler.length, kanalSayisi),
            ].filter(Boolean).join(" · ")}
          </p>
          {o.ortak && <p className="text-xs text-ink-3">{cevir("Read once: the same chart image was read in {0} ({1}). Nothing was sent to the converter.", o.ortak.kod, o.ortak.dosya)}</p>}
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 divide-y divide-line rounded-md border border-line text-sm" data-bolum="bl-parca">
            {k.grafikler.map((g) => (
              <li key={g.sira} className={SATIR}>
                <span>
                  <span className="block text-xs text-ink-3">{cevir("Pages")}</span>
                  <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa: g.sayfa })} target="_blank" rel="noreferrer">p.{g.sayfa}</a>
                </span>
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  <span className="block font-medium">{cevir("PWHT chart")}</span>
                  {g.ortakKanit && <span className="block text-xs text-ink-3">{cevir("Shared evidence · {0} jobs on the chart", g.isSayisi)}</span>}
                </span>
                <span className="col-span-2 min-w-0 [overflow-wrap:anywhere] @min-[40rem]:col-span-1">
                  <span className="block text-xs text-ink-3">{cevir("Place")}</span>
                  {y?.yerMetni && <span className="mr-1.5">{y.yerMetni}</span>}
                  {y && y.durum !== "yok"
                    ? <Etiket tone={durumTonu(y.durum)}>{y.durum === "kisi" ? cevir("this place") : cevir("proposed")}</Etiket>
                    : <span className="text-ink-2">{y ? pwhtNedenMetni(y.neden) : "—"}</span>}
                  {y?.durum === "oneri" && y.kaynak && <span className="mt-0.5 block text-xs text-ink-3">{pwhtNedenMetni(null, y.kaynak)}</span>}
                  {g.sira === 1 && yardim && y?.durum === "oneri" && y.yer && !hedefEsit(y.yer, hedef) && (
                    <Buton kucuk className="mt-1" disabled={mesgul !== null} mesgul={mesgul === "yer"} onClick={() => calistir("yer", () => yardim.yerKaydet(y.yer!))}>{cevir("Use this place")}</Buton>
                  )}
                </span>
              </li>
            ))}
            {disarida.length > 0 && (
              <li className={SATIR}>
                <span>
                  <span className="block text-xs text-ink-3">{cevir("Pages")}</span>
                  <span className="text-xs">p.{disarida.join(", ")}</span>
                </span>
                <span className="min-w-0">
                  <span className="block font-medium">{o.weldMapVar ? cevir("Weld map · not read as a PWHT chart") : cevir("Other pages · not read")}</span>
                </span>
                <span className="col-span-2 @min-[40rem]:col-span-1">—</span>
              </li>
            )}
          </ul>
          {yardim && sayfalar && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Buton kucuk disabled={mesgul !== null} onClick={() => yardim.sayfalar(sayfalar)}>{cevir("Use these pages")}</Buton>
              <span className="text-xs text-ink-3">{cevir("Nothing is filed until you confirm.")}</span>
            </div>
          )}

          {k.grafikler.map((g) => (
            <div key={g.sira} data-grafik={g.sira}>
              <AltBaslik>{cevir("Chart {0} (p.{1})", g.sira, g.sayfa)}</AltBaslik>
              <ul className="space-y-0.5 text-sm">
                {PWHT_GRAFIK_ALANLARI.filter((a) => a.alan !== "sayfa").map((a) => {
                  const d = g.degerler.find((x) => x.alan === a.alan);
                  const yazili = g.degerler.find((x) => x.alan === "yazili");
                  const deger = a.alan === "sure" && d?.deger
                    ? pwhtSureMetni(d.deger, yazili?.kesinlik === "kesin" && yazili.deger?.trim().toLowerCase() === "no")
                    : a.alan === "yazili" && d?.deger ? cevir(d.deger.trim().toLowerCase() === "yes" ? "yes" : d.deger.trim().toLowerCase() === "no" ? "no" : d.deger) : d?.deger;
                  return (
                    <li key={a.alan} className="flex gap-2">
                      <Nokta k={d?.kesinlik ?? "bos"} />
                      <span className="w-[13rem] shrink-0 text-ink-2">{cevir(pwhtEtiketi(a.alan))}</span>
                      <span className="min-w-0 [overflow-wrap:anywhere]">
                        {deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                        {sayfaLinki(d?.sayfa ?? null)}
                        {d?.yazildigi && d.yazildigi !== d.deger && <span className="block text-xs text-ink-3">{cevir("as written: {0}", d.yazildigi)}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {g.kanallar.length > 0 && (
                <>
                  <AltBaslik>{cevir("Channels on the chart")}</AltBaslik>
                  <div className="overflow-x-auto rounded-md border border-line">
                    <table className="w-full text-sm" data-bolum="bl-kars">
                      <thead><tr className="border-b border-line">
                        <th className={TABLO_BASI}>{cevir("Channel")}</th>
                        <th className={TABLO_BASI}>{cevir("On the chart")}</th>
                        <th className={TABLO_BASI}>{cevir("In OASIS")}</th>
                        <th className={TABLO_BASI}>{cevir("Result")}</th>
                      </tr></thead>
                      <tbody className="divide-y divide-line">
                        {g.kanallar.map((c) => {
                          const s = g.kanalSonuclari.find((x) => x.kanal === c.sira) ?? null;
                          const suphe = c.degerler.some((d) => d.kesinlik === "suphe");
                          return (
                            <tr key={c.sira} data-kanal={c.sira}>
                              <td className={cn(TABLO_HUCRESI, "font-mono")}>{c.sira}</td>
                              <td className={TABLO_HUCRESI}><span className="inline-flex items-center gap-1.5"><Nokta k={suphe ? "suphe" : "kesin"} />{pwhtKanalMetni(c.degerler)}</span></td>
                              <td className={TABLO_HUCRESI}>{s?.isMetni ?? "—"}</td>
                              <td className={TABLO_HUCRESI}>{s ? pwhtKanalSonucu(s) : "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          ))}

          {k.kaynaklar.length > 0 && (
            <>
              <AltBaslik>{cevir("Welds of this job and the chart")}</AltBaslik>
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm" data-bolum="bl-kaynak">
                  <thead><tr className="border-b border-line">
                    <th className={TABLO_BASI}>{cevir("Weld")}</th>
                    <th className={TABLO_BASI}>{cevir("On the chart")}</th>
                    <th className={TABLO_BASI}>{cevir("In OASIS (this job)")}</th>
                    <th className={TABLO_BASI}>{cevir("Result")}</th>
                  </tr></thead>
                  <tbody className="divide-y divide-line">
                    {k.kaynaklar.map((x) => (
                      <tr key={x.no} data-kaynak={x.no}>
                        <td className={cn(TABLO_HUCRESI, "font-mono")}>W{x.no}</td>
                        <td className={TABLO_HUCRESI}>{pwhtKaynakGrafikte(x)}</td>
                        <td className={TABLO_HUCRESI}>{pwhtKaynakOasis(x)}</td>
                        <td className={TABLO_HUCRESI}>{pwhtKaynakSonucu(x)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-1 text-xs text-ink-3">{cevir("The read values are shown next to the PWHT sign-off of OASIS; they do not replace it. The Weld map screen does not change.")}</p>
            </>
          )}
          <p className="mt-1 text-xs text-ink-3">{cevir("Temperature, operator and signatures are not read: open the page to see them.")}</p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {notlar.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {notlar.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <ul className="space-y-1.5">
                  {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={uyariYeri(u.hat)} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                </ul>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(OKUMA_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(OKUMA_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(OKUMA_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
